import { computed, Injectable, signal } from '@angular/core';
import { idiomaActual } from '@nucleo/i18n/idioma-actual';
import { DiccionarioIdiomas, formatearTexto, traducciones } from '@nucleo/i18n/traduccion-util';

const TEXTOS_CARGANDO: DiccionarioIdiomas<{
  cargando: string;
  cargandoModulo: string;
  cargandoNombre: string;
}> = {
  es: { cargando: 'Cargando...', cargandoModulo: 'Cargando módulo...', cargandoNombre: 'Cargando {nombre}...' },
  en: { cargando: 'Loading...', cargandoModulo: 'Loading module...', cargandoNombre: 'Loading {nombre}...' },
};

/**
 * Servicio transversal de gestión del estado de carga (Spinner / Loader).
 * Maneja peticiones concurrentes mediante un contador interno para evitar
 * apagar el cargador prematuramente cuando hay múltiples llamadas simultáneas.
 * Los mensajes se muestran en el idioma activo (se recalculan si cambia).
 */
@Injectable({
  providedIn: 'root',
})
export class CargandoService {
  private readonly t = traducciones(TEXTOS_CARGANDO);
  private readonly _contadorPeticiones = signal<number>(0);
  private readonly _cargandoModulo = signal<boolean>(false);
  /** Nombre del módulo que se descarga, en cada idioma (`data.nombre` de la ruta). */
  private readonly _nombreModulo = signal<DiccionarioIdiomas<string> | null>(null);

  /** Señal reactiva que indica si existe al menos una petición en curso o un módulo cargando */
  readonly estaCargando = computed<boolean>(
    () => this._contadorPeticiones() > 0 || this._cargandoModulo(),
  );

  /** Señal reactiva que indica si un módulo diferido está siendo cargado */
  readonly cargandoModulo = this._cargandoModulo.asReadonly();

  /** Mensaje contextual actual del estado de carga, en el idioma activo */
  readonly mensaje = computed<string>(() => {
    const textos = this.t();
    if (!this._cargandoModulo()) return textos.cargando;
    const nombre = this._nombreModulo()?.[idiomaActual()];
    return nombre ? formatearTexto(textos.cargandoNombre, { nombre }) : textos.cargandoModulo;
  });

  /** Señal de solo lectura con el conteo de peticiones activas */
  readonly contador = this._contadorPeticiones.asReadonly();

  /**
   * Registra el inicio de una operación asíncrona que requiere indicador de carga.
   */
  iniciar(): void {
    this._contadorPeticiones.update((contador) => contador + 1);
  }

  /**
   * Registra la finalización de una operación asíncrona.
   */
  finalizar(): void {
    this._contadorPeticiones.update((contador) => Math.max(0, contador - 1));
  }

  /**
   * Fuerza el restablecimiento del indicador de carga a cero (sin operaciones en curso).
   */
  limpiar(): void {
    this._contadorPeticiones.set(0);
    this._cargandoModulo.set(false);
    this._nombreModulo.set(null);
  }

  /**
   * Notifica el inicio de descarga / resolución de un módulo diferido.
   * @param nombre Nombre del módulo en cada idioma (`data.nombre` de la ruta), opcional.
   */
  iniciarCargaModulo(nombre?: DiccionarioIdiomas<string>): void {
    this._nombreModulo.set(nombre ?? null);
    this._cargandoModulo.set(true);
  }

  /**
   * Notifica que el módulo diferido finalizó su carga o falló la conexión.
   */
  finalizarCargaModulo(): void {
    this._cargandoModulo.set(false);
    this._nombreModulo.set(null);
  }
}

export { CargandoService as CargandoServicio };
